import { useCallback, useEffect, useRef, useState } from 'react';

export interface PredictionFormState {
  homeGoals: string;
  awayGoals: string;
  isBanker: boolean;
}

export interface SavedPrediction {
  match: { id: string };
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
}

interface UsePredictionAutoSubmitOptions {
  predictionForms: Record<string, PredictionFormState>;
  predictions: SavedPrediction[];
  submitting: Record<string, boolean>;
  onSubmit: (matchId: string) => void;
  eligibleMatchIds?: string[];
  preCountdownMs?: number;
  countdownSeconds?: number;
}

function bothScoresFilled(form: PredictionFormState | undefined): boolean {
  return Boolean(form && form.homeGoals !== '' && form.awayGoals !== '');
}

function shouldAutoSubmit(
  matchId: string,
  form: PredictionFormState | undefined,
  savedByMatchId: Map<string, SavedPrediction>
): boolean {
  if (!bothScoresFilled(form)) {
    return false;
  }

  // Only auto-submit new predictions, not updates to existing ones
  return !savedByMatchId.has(matchId);
}

export function usePredictionAutoSubmit({
  predictionForms,
  predictions,
  submitting,
  onSubmit,
  eligibleMatchIds,
  preCountdownMs = 1000,
  countdownSeconds = 5,
}: UsePredictionAutoSubmitOptions) {
  const [countdownValues, setCountdownValues] = useState<Record<string, number>>({});

  const onSubmitRef = useRef(onSubmit);
  const submittingRef = useRef(submitting);
  const predictionFormsRef = useRef(predictionForms);
  const prevFormsRef = useRef(predictionForms);
  const preCountdownTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const autoSubmitTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const countdownTimersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const activeCountdownMatchIdRef = useRef<string | null>(null);

  onSubmitRef.current = onSubmit;
  submittingRef.current = submitting;
  predictionFormsRef.current = predictionForms;

  const clearMatchTimers = useCallback((matchId: string) => {
    if (preCountdownTimeoutsRef.current[matchId]) {
      clearTimeout(preCountdownTimeoutsRef.current[matchId]);
      delete preCountdownTimeoutsRef.current[matchId];
    }
    if (autoSubmitTimeoutsRef.current[matchId]) {
      clearTimeout(autoSubmitTimeoutsRef.current[matchId]);
      delete autoSubmitTimeoutsRef.current[matchId];
    }
    if (countdownTimersRef.current[matchId]) {
      clearInterval(countdownTimersRef.current[matchId]);
      delete countdownTimersRef.current[matchId];
    }

    setCountdownValues(prev => {
      if (!(matchId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[matchId];
      return next;
    });

    if (activeCountdownMatchIdRef.current === matchId) {
      activeCountdownMatchIdRef.current = null;
    }
  }, []);

  const clearAllPreCountdowns = useCallback(() => {
    Object.keys(preCountdownTimeoutsRef.current).forEach(matchId => {
      clearTimeout(preCountdownTimeoutsRef.current[matchId]);
      delete preCountdownTimeoutsRef.current[matchId];
    });
  }, []);

  const startCountdownForMatch = useCallback((matchId: string) => {
    clearMatchTimers(matchId);

    activeCountdownMatchIdRef.current = matchId;
    setCountdownValues(prev => ({ ...prev, [matchId]: countdownSeconds }));

    const countdownTimer = setInterval(() => {
      setCountdownValues(prev => {
        const currentValue = prev[matchId];
        if (currentValue === undefined || currentValue <= 1) {
          clearInterval(countdownTimersRef.current[matchId]);
          delete countdownTimersRef.current[matchId];
          return prev;
        }
        return { ...prev, [matchId]: currentValue - 1 };
      });
    }, 1000);

    countdownTimersRef.current[matchId] = countdownTimer;
    autoSubmitTimeoutsRef.current[matchId] = setTimeout(() => {
      onSubmitRef.current(matchId);
      clearMatchTimers(matchId);
    }, countdownSeconds * 1000);
  }, [clearMatchTimers, countdownSeconds]);

  const cancelAutoSubmit = useCallback((matchId: string) => {
    clearMatchTimers(matchId);
  }, [clearMatchTimers]);

  useEffect(() => {
    clearAllPreCountdowns();

    const changedMatchIds = new Set<string>();
    const allMatchIds = new Set([
      ...Object.keys(prevFormsRef.current),
      ...Object.keys(predictionForms),
    ]);

    allMatchIds.forEach(matchId => {
      const prev = prevFormsRef.current[matchId];
      const curr = predictionForms[matchId];
      if (
        prev?.homeGoals !== curr?.homeGoals ||
        prev?.awayGoals !== curr?.awayGoals ||
        prev?.isBanker !== curr?.isBanker
      ) {
        changedMatchIds.add(matchId);
      }
    });
    prevFormsRef.current = predictionForms;

    changedMatchIds.forEach(matchId => {
      clearMatchTimers(matchId);
    });

    const savedByMatchId = new Map(predictions.map(prediction => [prediction.match.id, prediction]));
    const eligibleSet = eligibleMatchIds ? new Set(eligibleMatchIds) : null;

    const matchesNeedingCountdown = Object.entries(predictionForms).filter(([matchId, form]) => {
      if (eligibleSet && !eligibleSet.has(matchId)) {
        return false;
      }
      if (submittingRef.current[matchId]) {
        return false;
      }
      if (preCountdownTimeoutsRef.current[matchId]) {
        return false;
      }
      if (autoSubmitTimeoutsRef.current[matchId]) {
        return false;
      }
      return shouldAutoSubmit(matchId, form, savedByMatchId);
    });

    if (matchesNeedingCountdown.length === 0) {
      return;
    }

    const [matchId] = matchesNeedingCountdown[0];
    const activeCountdownMatchId = activeCountdownMatchIdRef.current;

    if (activeCountdownMatchId && activeCountdownMatchId !== matchId) {
      onSubmitRef.current(activeCountdownMatchId);
      clearMatchTimers(activeCountdownMatchId);
      return;
    }

    preCountdownTimeoutsRef.current[matchId] = setTimeout(() => {
      delete preCountdownTimeoutsRef.current[matchId];
      startCountdownForMatch(matchId);
    }, preCountdownMs);
  }, [
    predictionForms,
    predictions,
    eligibleMatchIds,
    clearAllPreCountdowns,
    clearMatchTimers,
    startCountdownForMatch,
    preCountdownMs,
  ]);

  useEffect(() => {
    return () => {
      Object.values(preCountdownTimeoutsRef.current).forEach(clearTimeout);
      Object.values(autoSubmitTimeoutsRef.current).forEach(clearTimeout);
      Object.values(countdownTimersRef.current).forEach(clearInterval);
    };
  }, []);

  const hasActiveCountdown = useCallback(
    (matchId: string) => Boolean(countdownValues[matchId]),
    [countdownValues]
  );

  return {
    countdownValues,
    cancelAutoSubmit,
    hasActiveCountdown,
  };
}
